import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {ProgressSpinner} from "primereact/progressspinner";
import {SelectButton} from "primereact/selectbutton";
import api from "../../../system/api";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import Span from "../../../system/components/span";
import {useTranslate} from "../../../system/useTranslate";
import "./knowledge.css";
import {RichTextEditor, RichTextView} from "../../../system/components/rich-text";

// Project knowledge (Zofino master spec, section 5): the team's ideas and notes under the project.
// Keep-style inline composer (no modal), two columns, votes on ideas, in-place expand, text / person / date filters.

const DAY = 24 * 60 * 60 * 1000;
const PERIODS = [7, 30, 90];

function KnowledgeCard({item, expanded, onToggle, onVote, onSave, onArchive, translate}) {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(item.title || "");
    const [content, setContent] = useState(item.content || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!expanded) setEditing(false);
    }, [expanded]);

    const save = async () => {
        setSaving(true);
        const ok = await onSave(item, {title, content});
        setSaving(false);
        if (ok) setEditing(false);
    };

    return (
        <article className={`knowledge-card ${expanded ? "is-expanded" : ""}`} onClick={() => !editing && onToggle(item)}>
            <header className="flex align-items-center gap-2">
                <Avatar memberData={item.author?.member} size="small"/>
                <span className="text-xs font-medium">{item.author?.name || translate("project.knowledge.unknown-author")}</span>
                <Span type="TIME_SINCE" spanClassName="text-xs text-500 mr-auto">{item.createdAt}</Span>
            </header>
            {editing ? (
                <div className="flex flex-column gap-2 mt-2" onClick={(event) => event.stopPropagation()}>
                    <InputText value={title} onChange={(event) => setTitle(event.target.value)} className="text-sm" placeholder={translate("project.knowledge.title")}/>
                    <RichTextEditor value={content} onChange={setContent} minHeight="5rem"/>
                    <div className="flex gap-2 justify-content-end">
                        <Button type="button" text className="text-xs p-2" label={translate("cancel")} onClick={() => setEditing(false)}/>
                        <Button type="button" className="text-xs p-2" label={translate("save")} loading={saving} onClick={save}/>
                    </div>
                </div>
            ) : (
                <>
                    {item.title && <div className="knowledge-title">{item.title}</div>}
                    {item.content && <RichTextView className={`knowledge-content ${expanded ? "" : "is-clamped"}`} value={item.content}/>}
                </>
            )}
            <footer className="flex align-items-center gap-1 mt-2" onClick={(event) => event.stopPropagation()}>
                {item.kind === "idea" && (
                    <>
                        <Button
                            type="button"
                            text
                            className={`knowledge-vote ${item.myVote === 1 ? "is-active" : ""}`}
                            icon={<Icon icon="mi mi:thumb_up" className="text-base"/>}
                            label={String(item.likes || 0)}
                            aria-label={translate("project.knowledge.like")}
                            onClick={() => onVote(item, item.myVote === 1 ? 0 : 1)}
                        />
                        <Button
                            type="button"
                            text
                            className={`knowledge-vote is-down ${item.myVote === -1 ? "is-active" : ""}`}
                            icon={<Icon icon="mi mi:thumb_down" className="text-base"/>}
                            label={String(item.dislikes || 0)}
                            aria-label={translate("project.knowledge.dislike")}
                            onClick={() => onVote(item, item.myVote === -1 ? 0 : -1)}
                        />
                    </>
                )}
                {expanded && item.canEdit && !editing && (
                    <span className="mr-auto flex gap-1">
                        <Button type="button" text className="text-xs p-1" icon={<Icon icon="mi mi:edit" className="text-base"/>} aria-label={translate("edit")} tooltip={translate("edit")} onClick={() => setEditing(true)}/>
                        <Button type="button" text severity="danger" className="text-xs p-1" icon={<Icon icon="mi mi:delete" className="text-base"/>} aria-label={translate("delete")} tooltip={translate("delete")} onClick={() => onArchive(item)}/>
                    </span>
                )}
            </footer>
        </article>
    );
}

function Knowledge({projectData}) {
    const translate = useTranslate();
    const workspaceMembershipData = useSelector((state) => state.app.workspaceMembershipData);
    const workspaceId = workspaceMembershipData?.workspace?.documentId;
    const projectId = projectData?.documentId;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [author, setAuthor] = useState(null);
    const [period, setPeriod] = useState(null);
    const [expandedKey, setExpandedKey] = useState(null);
    const [composerOpen, setComposerOpen] = useState(false);
    const [draft, setDraft] = useState({kind: "idea", title: "", content: ""});
    const [saving, setSaving] = useState(false);
    const composerRef = useRef(null);

    const config = useMemo(() => ({headers: {workspace: workspaceId}}), [workspaceId]);
    const errorText = (requestError) => translate(requestError?.normalizedError?.message || requestError?.message || "errors.unexpected");

    const load = useCallback(async () => {
        if (!projectId || !workspaceId) return;
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (author) params.author = author;
            if (period) params.since = new Date(Date.now() - period * DAY).toISOString();
            const {data} = await api.get(`/projects/${projectId}/knowledge`, {...config, params, suppressGlobalErrorHandler: true});
            setItems(data?.data || []);
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setLoading(false);
        }
        // translate is stable enough here; errorText only formats the message
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [author, config, period, projectId, search, workspaceId]);

    useEffect(() => {
        const timer = window.setTimeout(load, search ? 350 : 0);
        return () => window.clearTimeout(timer);
    }, [load, search]);

    // close the Keep-style composer when clicking outside it with nothing written
    useEffect(() => {
        if (!composerOpen) return undefined;
        const close = (event) => {
            if (composerRef.current?.contains(event.target)) return;
            if (!draft.title.trim() && !draft.content.trim()) setComposerOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [composerOpen, draft]);

    const replaceItem = (updated) => setItems((current) => current.map((item) => (item.documentId === updated.documentId ? updated : item)));

    const submit = async () => {
        if (!draft.title.trim() && !draft.content.trim()) return;
        setSaving(true);
        try {
            const {data} = await api.post(`/projects/${projectId}/knowledge`, {data: draft}, config);
            if (data?.data) setItems((current) => [data.data, ...current]);
            setDraft({kind: draft.kind, title: "", content: ""});
            setComposerOpen(false);
        } catch {
            // the global error toast explains the failure
        } finally {
            setSaving(false);
        }
    };

    const vote = async (item, value) => {
        try {
            const {data} = await api.post(`/project-knowledge/${item.documentId}/vote`, {data: {value}}, config);
            if (data?.data) replaceItem(data.data);
        } catch {
            // the global error toast explains the failure
        }
    };

    const save = async (item, changes) => {
        try {
            const {data} = await api.put(`/project-knowledge/${item.documentId}`, {data: changes}, config);
            if (data?.data) replaceItem(data.data);
            return true;
        } catch {
            return false;
        }
    };

    const archive = async (item) => {
        try {
            await api.delete(`/project-knowledge/${item.documentId}`, config);
            setItems((current) => current.filter((entry) => entry.documentId !== item.documentId));
        } catch {
            // the global error toast explains the failure
        }
    };

    const memberOptions = useMemo(() => (workspaceMembershipData?.workspace?.workspace_memberships || [])
        .map((membership) => ({label: membership?.member?.fullname || membership?.member?.username, value: membership?.documentId}))
        .filter((option) => option.label && option.value), [workspaceMembershipData]);
    const periodOptions = PERIODS.map((days) => ({label: translate(`project.knowledge.period.${days}`), value: days}));
    const kindOptions = [
        {label: translate("project.knowledge.idea"), value: "idea"},
        {label: translate("project.knowledge.note"), value: "note"},
    ];
    const ideas = items.filter((item) => item.kind === "idea");
    const notes = items.filter((item) => item.kind === "note");
    const filtersActive = Boolean(search.trim() || author || period);

    const column = (kind, list) => (
        <section className="knowledge-column">
            <h3 className="knowledge-column-title">
                <Icon icon={kind === "idea" ? "mi mi:lightbulb" : "mi mi:sticky_note_2"} className="text-lg"/>
                {translate(kind === "idea" ? "project.knowledge.ideas" : "project.knowledge.notes")}
                <span className="text-500 font-normal">({list.length.toLocaleString("fa-IR")})</span>
            </h3>
            {!list.length && !loading && (
                <div className="knowledge-empty">{translate(filtersActive ? "project.knowledge.empty-filtered" : `project.knowledge.empty-${kind}`)}</div>
            )}
            {list.map((item) => (
                <KnowledgeCard
                    key={item.documentId}
                    item={item}
                    expanded={expandedKey === item.documentId}
                    onToggle={(target) => setExpandedKey((current) => (current === target.documentId ? null : target.documentId))}
                    onVote={vote}
                    onSave={save}
                    onArchive={archive}
                    translate={translate}
                />
            ))}
        </section>
    );

    return (
        <div className="knowledge-page" dir="rtl">
            <div ref={composerRef} className={`knowledge-composer ${composerOpen ? "is-open" : ""}`}>
                {!composerOpen ? (
                    <button type="button" className="knowledge-composer-trigger" onClick={() => setComposerOpen(true)}>
                        <Icon icon="mi mi:edit_note" className="text-xl"/>
                        {translate("project.knowledge.composer-placeholder")}
                    </button>
                ) : (
                    <div className="flex flex-column gap-2">
                        <SelectButton
                            value={draft.kind}
                            options={kindOptions}
                            allowEmpty={false}
                            onChange={(event) => setDraft((current) => ({...current, kind: event.value}))}
                            pt={{button: {className: "text-xs py-2 px-3"}}}
                        />
                        <InputText
                            value={draft.title}
                            onChange={(event) => setDraft((current) => ({...current, title: event.target.value}))}
                            placeholder={translate("project.knowledge.title")}
                            className="text-sm border-none shadow-none px-1"
                            maxLength={200}
                        />
                        <RichTextEditor
                            autoFocus
                            value={draft.content}
                            onChange={(html) => setDraft((current) => ({...current, content: html}))}
                            placeholder={translate(draft.kind === "idea" ? "project.knowledge.idea-placeholder" : "project.knowledge.note-placeholder")}
                            minHeight="5rem"
                        />
                        <div className="flex gap-2 justify-content-end">
                            <Button type="button" text className="text-xs p-2" label={translate("cancel")} onClick={() => { setComposerOpen(false); setDraft({kind: draft.kind, title: "", content: ""}); }}/>
                            <Button
                                type="button"
                                className="text-xs p-2"
                                label={translate("project.knowledge.submit")}
                                loading={saving}
                                disabled={!draft.title.trim() && !draft.content.trim()}
                                onClick={submit}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="knowledge-filters">
                <IconField className="flex-1 min-w-0" style={{maxWidth: "20rem"}}>
                    <InputIcon className="pi pi-search"/>
                    <InputText value={search} onChange={(event) => setSearch(event.target.value)} placeholder={translate("project.knowledge.search")} className="w-full text-xs"/>
                </IconField>
                <Dropdown value={author} options={memberOptions} onChange={(event) => setAuthor(event.value ?? null)} placeholder={translate("project.knowledge.author")} filter showClear className="text-xs knowledge-filter"/>
                <Dropdown value={period} options={periodOptions} onChange={(event) => setPeriod(event.value ?? null)} placeholder={translate("project.knowledge.date")} showClear className="text-xs knowledge-filter"/>
                {loading && <ProgressSpinner style={{width: "1.25rem", height: "1.25rem"}} strokeWidth="4"/>}
            </div>

            {error && <div className="text-xs text-red-500 px-1">{error}</div>}

            <div className="knowledge-columns">
                {column("idea", ideas)}
                {column("note", notes)}
            </div>
        </div>
    );
}

export default Knowledge;
